/**
 * Inferencias de visualización para relojes Chrono24 cuando `specifications`
 * no tiene booleanos (p. ej. datos antiguos o API con formato distinto).
 * Usa el JSON crudo guardado en `reloj.data` como respaldo.
 */

function normalizeEstadoRaw(raw) {
  if (raw == null) return "";
  if (typeof raw === "string") return raw.trim();
  if (typeof raw === "object" && !Array.isArray(raw)) {
    if (typeof raw.usado === "boolean") {
      return raw.usado ? "Usado" : "Nuevo";
    }
    if (raw.label) return String(raw.label).trim();
    if (raw.text) return String(raw.text).trim();
    if (raw.value) return String(raw.value).trim();
    const desc =
      raw.descripcion ?? raw.description ?? raw.calificacion ?? raw.grade;
    if (desc != null && String(desc).trim()) return String(desc).trim();
    const rest = Object.entries(raw)
      .filter(
        ([k, v]) =>
          typeof v === "string" &&
          String(v).trim() &&
          ![
            "label",
            "text",
            "value",
            "descripcion",
            "description",
            "calificacion",
            "grade",
            "usado",
          ].includes(k)
      )
      .map(([, v]) => String(v).trim())
      .join(" ");
    if (rest) return rest;
    return "";
  }
  return String(raw).trim();
}

function normalizeContenidoRaw(raw) {
  if (raw == null) return "";
  if (typeof raw === "string") return raw.trim();
  if (Array.isArray(raw)) {
    return raw.map((x) => String(x)).join(" ").trim();
  }
  if (typeof raw === "object") {
    if (typeof raw.estuche === "boolean" || typeof raw.documento === "boolean") {
      return raw;
    }
    if (Array.isArray(raw.items)) {
      return raw.items.map((x) => String(x)).join(" ").trim();
    }
    if (raw.text) return String(raw.text).trim();
    if (raw.description) return String(raw.description).trim();
  }
  return String(raw).trim();
}

function freeTextBlob(reloj) {
  const d = reloj?.data || {};
  return [
    d.description,
    reloj?.description,
    reloj?.name,
    d.name,
    d.title,
    typeof d.headline === "string" ? d.headline : "",
  ]
    .filter(Boolean)
    .join("\n");
}

/** Negaciones explícitas en título/descripción (prioridad alta). */
function inferContenidoNegativesFromFreeText(texto) {
  const c = String(texto || "").trim();
  if (!c) return { estuche: null, documento: null };
  const cl = c.toLowerCase();

  if (
    /\bout\s+the\s+horn\b|\bsolo\s+reloj\b|\bhead\s*only\b|\buhr\s+allein\b|\bnur\s+uhr\b|\bnur\s+die\s+uhr\b|\bonly\s+the\s+watch\b|\bwatch\s+only\b/i.test(
      cl
    )
  ) {
    return { estuche: false, documento: false };
  }

  let estuche = null;
  let documento = null;

  if (/\bohne\s+box\s+und\s+ohne\s+papieren\b/i.test(cl)) {
    estuche = false;
    documento = false;
  }
  if (/\bohne\s+(original-?)?box\b|\bohne\s+etui\b|\bkeine\s+box\b/i.test(c)) {
    estuche = false;
  }
  if (
    /\bohne\s+papieren\b|\bohne\s+papiere\b|\bohne\s+unterlagen\b|\bkeine\s+papiere\b|\bkeine\s+unterlagen\b/i.test(
      c
    )
  ) {
    documento = false;
  }
  if (/\bsin\s+documentos?\s+originales\b/i.test(c)) documento = false;
  if (/\bsin\s+estuche\s+original\b/i.test(c)) estuche = false;
  if (
    /\bsin\s+(estuche|caja)\b|\bwithout\s+box\b|\bno\s+box\b|\bohne\s+(box|etui|schachtel)\b|\bsans\s+bo[iî]te\b/i.test(
      c
    )
  ) {
    estuche = false;
  }
  if (
    /\bsin\s+(papeles|documentos|documentación)\b|\bwithout\s+papers\b|\bno\s+papers\b/i.test(
      c
    )
  ) {
    documento = false;
  }
  if (/\bno\s+incluye\s+(caja|estuche|box)\b/i.test(c)) estuche = false;
  if (/\bno\s+incluye\s+(papeles|documentos|documentación)\b/i.test(c)) documento = false;

  return { estuche, documento };
}

/** Positivos en texto libre (solo si no hay ya false). */
function inferContenidoPositivesFromFreeText(texto) {
  const c = String(texto || "").trim();
  if (!c) return { estuche: null, documento: null };
  const cl = c.toLowerCase();

  if (
    /\bfull\s*set\b|\bjuego\s+completo\b|\bbox\s*(and|&|\+|,)\s*papers\b|\bcaja\s+y\s+papeles\b|\bmit\s+box\s+und\s+papieren\b|\bcon\s+caja\s+y\s+papeles\b/i.test(
      c
    )
  ) {
    return { estuche: true, documento: true };
  }

  let estuche = null;
  let documento = null;

  if (
    /\b(?<!no\s)(con\s+caja|con\s+estuche|caja\s+original|estuche\s+original|with\s+box|incl\.?\s+box|mit\s+(etui|box|schachtel))\b/i.test(
      cl
    )
  ) {
    estuche = true;
  }
  if (
    /\b(?<!no\s)(incluye\s+(estuche|caja))\b/i.test(cl)
  ) {
    estuche = true;
  }
  if (
    /\b(con\s+papeles|con\s+documentos|with\s+papers|mit\s+papieren|papers\s+included)\b/i.test(
      cl
    )
  ) {
    documento = true;
  }
  if (
    /\b(certificado\s+de\s+(garantía|origen|autenticidad|calidad)|papeles\s+de\s+garantía)\b/i.test(
      cl
    )
  ) {
    documento = true;
  }

  return { estuche, documento };
}

/** Año de fabricación/referencia en anuncio (pasado) ⇒ mercado secundario. */
function inferUsadoFromYearInText(sl) {
  const cy = new Date().getFullYear();
  const re =
    /\b(?:año|year|jahrgang|ann[ée]e)\s*:?\s*(19\d{2}|20\d{2})\b|\b(?:fabricaci[óo]n|manufacture)\s*:?\s*(19\d{2}|20\d{2})\b/i;
  const m = sl.match(re);
  if (!m) return null;
  const y = parseInt(m[1] || m[2], 10);
  if (y < 1950 || y > cy) return null;
  if (y <= cy - 1) return true;
  return null;
}

/** @returns {boolean|null} */
export function inferUsadoFromEstadoString(texto) {
  const s = String(texto || "").trim();
  if (!s) return null;
  const sl = s.toLowerCase();

  // 1) Sin estrenar / stock nuevo (antes que «vintage» marque usado)
  if (
    /\bNOS\b|\bnew\s+old\s+stock\b|\bdead\s+stock\b|\bBNIB\b|\bNWOT\b|\bunworn\b|\bnever\s+worn\b|\bbrand[\s-]*new\b|\bneu\b|\bneuwertig\b|\bungetragen\b|\bsin\s+estrenar\b|\bsin\s+uso\b|\bnuevo\s+y\s+sin\s+usar\b|\bzustand:\s*neu\b|\bcondition:\s*unworn\b|\bartikelzustand:\s*neu\b|\bitem\s+condition:\s*(new|unworn|mint)\b|\bestado(?:\s+del\s+reloj)?\s*:\s*(nuevo|sin\s+estrenar)\b|\bmint\s+condition\b|\bstickers?\s+on\b|\bwith\s+stickers\b/i.test(
      sl
    )
  ) {
    return false;
  }
  if (
    /sin estrenar|sin uso|como nuevo|excelente estado|muy buen estado|buen estado|nuevo(?!\s+y\s+sin)/i.test(
      sl
    )
  ) {
    return false;
  }
  if (
    /excellent\s+condition|very\s+good\s+condition|good\s+condition|like\s+new|new\s+condition|unworn\s+condition/i.test(
      sl
    )
  ) {
    return false;
  }

  // 2) Usado (Chrono24 en EN/DE y títulos con historia de pieza)
  if (
    /\busado\b|\bused\b|\bworn\b|\bpre[\s-]*owned\b|\boccasion\b|\bd['\u2019]?\s*occasion\b|\bgebraucht\b|\bgetragen\b|\bsecond[\s-]*hand\b|\bzustand:\s*gebraucht\b|\bcondition:\s*worn\b|\bartikelzustand:\s*gebraucht\b|\bitem\s+condition:\s*(pre-?owned|used)\b|\bestado(?:\s+del\s+reloj)?\s*:\s*usado\b/i.test(
      sl
    )
  ) {
    return true;
  }
  if (
    /\bvintage\b|\bserviced\b|\bservice\s+history\b|\bservice\s+record\b|\büberholt\b|\bgeneralüberholt\b|\brevidiert\b|\büberarbeitet\b|\boverhauled\b|\brefurbished\b|\bunpolished\b|\brepolished\b|\btropical\b|\bpatina\b|\btritium\b|\bradium\b|\bnear\s+mint\b|\bpreviously\s+enjoyed\b|\bwell[\s-]*worn\b|\bpolished\s+case\b|\breplacement\s+dial\b/i.test(
      sl
    )
  ) {
    return true;
  }
  const yearInf = inferUsadoFromYearInText(sl);
  if (yearInf !== null) return yearInf;
  return null;
}

/**
 * @param {string|object} raw
 * @returns {{ estuche: boolean|null, documento: boolean|null }}
 */
export function inferContenidoEntrega(raw) {
  const normalized = normalizeContenidoRaw(raw);
  if (normalized && typeof normalized === "object") {
    return {
      estuche:
        typeof normalized.estuche === "boolean" ? normalized.estuche : null,
      documento:
        typeof normalized.documento === "boolean"
          ? normalized.documento
          : null,
    };
  }
  const c = String(normalized || "");
  if (!c) return { estuche: null, documento: null };
  const cl = c.toLowerCase();

  let estuche = null;
  const regexEstuche = /(Con estuche original|Sin estuche original)/i;
  const mEst = c.match(regexEstuche);
  if (mEst) {
    estuche = mEst[1].toLowerCase() === "con estuche original";
  } else if (
    /\b(sin estuche|sin caja|without box|ohne box|sans boîte|ohne\s+original-?box|keine\s+box)\b/i.test(
      cl
    )
  ) {
    estuche = false;
  } else if (
    /\b(?<!no\s)(con\s+estuche|con\s+caja|caja\s+original|estuche\s+original|incluye\s+(estuche|caja)|with\s+box|with\s+papers\s+and\s+box|mit\s+(etui|box|schachtel)|original-?box)\b/i.test(
      cl
    )
  ) {
    estuche = true;
  }

  let documento = null;
  const regexDoc =
    /(con documentos originales|sin documentos originales)/i;
  const mDoc = c.match(regexDoc);
  if (mDoc) {
    documento = mDoc[1].toLowerCase() === "con documentos originales";
  } else if (/\b(sin documentos|sin papeles|sin documentación|ohne papiere|no papers)\b/i.test(cl)) {
    documento = false;
  } else if (
    /\b(con documentos|con papeles|documentación|papeles de garantía|full set|juego completo|with papers|mit papieren)\b/i.test(
      cl
    )
  ) {
    documento = true;
  }

  return { estuche, documento };
}

/**
 * Infiere estuche/documento desde texto libre (compat: delega en negativos+positivos).
 * @returns {{ estuche: boolean|null, documento: boolean|null }}
 */
export function inferContenidoFromFreeText(texto) {
  const neg = inferContenidoNegativesFromFreeText(texto);
  const pos = inferContenidoPositivesFromFreeText(texto);
  return {
    estuche: neg.estuche != null ? neg.estuche : pos.estuche,
    documento: neg.documento != null ? neg.documento : pos.documento,
  };
}

/**
 * Orden: negaciones en descripción → booleanos en specifications → texto estructurado API → positivos en descripción.
 * Evita "certificado" suelto y "no incluye caja" mal interpretados.
 */
export function contenidoMergeFromReloj(reloj) {
  const ft = freeTextBlob(reloj);
  const raw =
    reloj?.data?.specifications?.contenido_de_la_entrega ??
    reloj?.data?.contenido_de_la_entrega;

  let rawStr = "";
  if (typeof raw === "string") rawStr = raw.trim();
  else if (raw && typeof raw === "object") {
    rawStr = [raw.text, raw.description, Array.isArray(raw.items) ? raw.items.join(" ") : ""]
      .filter(Boolean)
      .join(" ")
      .trim();
  }

  const neg = inferContenidoNegativesFromFreeText([ft, rawStr].filter(Boolean).join("\n"));
  const spec = reloj?.specifications?.contenido_de_la_entrega || {};

  let estuche = neg.estuche;
  let documento = neg.documento;

  if (estuche == null && typeof spec.estuche === "boolean") estuche = spec.estuche;
  if (documento == null && typeof spec.documento === "boolean") {
    documento = spec.documento;
  }

  const fromStruct = inferContenidoEntrega(raw);
  if (estuche == null) estuche = fromStruct.estuche;
  if (documento == null) documento = fromStruct.documento;

  const pos = inferContenidoPositivesFromFreeText(ft);
  if (estuche == null) estuche = pos.estuche;
  if (documento == null) documento = pos.documento;

  return { estuche, documento };
}

/**
 * @returns {boolean|null}
 */
export function relojUsadoBoolean(reloj) {
  const u = reloj?.specifications?.estado?.usado;
  if (typeof u === "boolean") return u;
  // Preferir estado persistido en el documento; `data` a veces no trae `specifications`.
  const raw =
    reloj?.specifications?.estado ??
    reloj?.data?.specifications?.estado ??
    reloj?.data?.estado ??
    reloj?.data?.condition ??
    reloj?.data?.state;
  const str = normalizeEstadoRaw(raw);
  if (str) {
    const inf = inferUsadoFromEstadoString(str);
    if (inf !== null) return inf;
  }
  const descSpec =
    reloj?.specifications?.estado?.descripcion ??
    reloj?.specifications?.estado?.description;
  if (descSpec && String(descSpec).trim()) {
    const inf = inferUsadoFromEstadoString(String(descSpec).trim());
    if (inf !== null) return inf;
  }
  const blob = freeTextBlob(reloj);
  if (blob) {
    const infBlob = inferUsadoFromEstadoString(blob);
    if (infBlob !== null) return infBlob;
    if (reloj?.origin === "chrono24" && blob.trim().length > 28) return true;
  }
  return null;
}

/**
 * Etiqueta "Usado" | "Sin Estrenar" | cadena vacía si no se puede deducir (sin "---").
 */
export function estadoCondicionLabelFromReloj(reloj) {
  const inf = relojUsadoBoolean(reloj);
  if (inf === true) return "Usado";
  if (inf === false) return "Sin Estrenar";
  return "";
}

export function siNoFromContenido(reloj, campo) {
  const merged = contenidoMergeFromReloj(reloj);
  const w = merged[campo];
  if (w === true) return "Si";
  if (w === false) return "No";
  return "No";
}

/** Booleano para estadísticas: desconocido se trata como false (alineado con lista: "No"). */
export function contenidoBooleanFromReloj(reloj, campo) {
  const merged = contenidoMergeFromReloj(reloj);
  const w = merged[campo];
  if (w === true) return true;
  return false;
}
